import { omit } from 'lodash'
import React, {
  FC,
  HTMLProps,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { MdMoreVert } from 'react-icons/md'

import useHover from 'src/hooks/useHover'
import useWindowSize from 'src/hooks/useWindowSize'
import { cn } from 'src/lib/cn'
import { ReactCallBackFunction } from 'src/types/CallbackFunction'
import Direction from 'src/types/DirectionInterface'
import PopupFixedPropsInterface from 'src/utils/popup/PopupFixedPropsInterface'
import { getElLocationStyles } from 'src/utils/popupUtils'
import PopupButtonProvider from './PopupButtonProvider'

import * as styles from './PopupButton.scss'

const MOUSE_LEAVE_DELAY = 100

interface PopupButtonProps {
  loading?: boolean
  title?: string
  disabled?: boolean
  buttonComponent?: ReactElement<{ toggled: boolean; loading: boolean }>
  popupComponent: ReactElement<{ toggled: boolean; togglePopup: VoidFunction }>
  className?: string
  classes?: {
    Popup?: string
    divHover?: string
  }
  popupFixedProps?: PopupFixedPropsInterface
  togglePopup?: (isOpen: boolean) => void
  isLazy?: boolean
  isOpen?: boolean | null
  buttonProps?: HTMLProps<HTMLDivElement> & { 'data-testid'?: string }
  buttonClassName?: string
  closeOnScroll?: boolean
  needToPassChildToggleProps?: boolean
  openOnHover?: boolean // Open popup on hover
  'data-testid'?: string
  'data-pendo'?: string
  mouseLeaveActiveDirection?: Direction
  popoverProps?: HTMLProps<HTMLDivElement>
  usePortal?: boolean
}

const PopupButton: FC<PopupButtonProps> = ({
  loading = false,
  title,
  popupComponent,
  buttonComponent,
  disabled,
  classes = {},
  className,
  popupFixedProps,
  togglePopup: togglePopupProps,
  isLazy = false,
  isOpen: isOpenProp = null,
  buttonProps,
  buttonClassName,
  closeOnScroll,
  needToPassChildToggleProps = true,
  openOnHover,
  mouseLeaveActiveDirection,
  'data-testid': dataTestId = 'popup-button',
  'data-pendo': dataPendo,
  popoverProps,
  usePortal = false,
}) => {
  const [isOpenState, setIsOpenState] = useState(Boolean(isOpenProp))
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLDivElement | null>(null)

  const { hoverProps: componentHoverProps, delayedHover: componentHovered } = useHover({
    mouseLeaveDelay: openOnHover ? MOUSE_LEAVE_DELAY : undefined,
    mouseLeaveActiveDirection: openOnHover ? mouseLeaveActiveDirection : undefined,
  })

  useEffect(() => {
    setIsOpenState(isOpenProp ?? false)
  }, [isOpenProp])

  const handleSetIsOpen: ReactCallBackFunction<boolean> = useCallback(open => {
    setIsOpenState(s => (typeof open === 'function' ? open(s) : open))
  }, [])

  const isFirstRenderRef = useRef(true)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }
    togglePopupProps?.(isOpenState)
  }, [isOpenState, togglePopupProps])

  useEffect(() => {
    if (openOnHover) {
      handleSetIsOpen(componentHovered)
    }
  }, [componentHovered, handleSetIsOpen, openOnHover])

  const isOpen = isOpenProp ?? isOpenState

  const handleClickOutside = useCallback(
    event => {
      if (
        wrapperRef.current &&
        buttonRef.current &&
        !wrapperRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target) &&
        isOpenState
      ) {
        handleSetIsOpen(false)
      }
    },
    [wrapperRef, buttonRef, isOpen],
  )

  const togglePopup = () => {
    handleSetIsOpen(currentIsOpen => !currentIsOpen)
  }

  const handleTogglePopup = useCallback((isOpenArg: boolean) => {
    handleSetIsOpen(isOpenArg)
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)

    if (closeOnScroll) {
      document.addEventListener('scroll', handleClickOutside, usePortal)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (closeOnScroll) {
        document.removeEventListener('scroll', handleClickOutside, usePortal)
      }
    }
  }, [handleClickOutside, closeOnScroll])

  const buttonClasses = cn(styles.Button, {
    [styles.ButtonActive]: isOpen,
    [styles.ButtonLoading]: loading,
  })

  const { width } = useWindowSize()

  const fixedStyles = getElLocationStyles(popupFixedProps, buttonRef, width)
  const { hoverProps, isHovered } = useHover()

  const [autoPlacementStyle, setAutoPlacementStyle] = useState<React.CSSProperties>({})

  const computePlacementStyle = useCallback(() => {
    if (!isOpen || popupFixedProps || !wrapperRef.current || !buttonRef.current) {
      if (!isOpen) {
        setAutoPlacementStyle({})
      }
      return
    }

    const popupRect = wrapperRef.current.getBoundingClientRect()
    const buttonRect = buttonRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    const spaceBelow = viewportHeight - buttonRect.bottom
    const needsFlipVertical = popupRect.height > spaceBelow && buttonRect.top > spaceBelow

    const needsFlipHorizontal = popupRect.right > viewportWidth

    if (!usePortal && !needsFlipVertical && !needsFlipHorizontal) {
      setAutoPlacementStyle({})
      return
    }

    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 14440,
    }

    const POPUP_GAP = 12
    const VIEWPORT_PADDING = 8

    if (needsFlipVertical) {
      const idealTop = buttonRect.top - popupRect.height - POPUP_GAP
      if (idealTop < VIEWPORT_PADDING) {
        style.top = VIEWPORT_PADDING
        style.maxHeight = buttonRect.top - POPUP_GAP - VIEWPORT_PADDING
        style.overflowY = 'auto'
      } else {
        style.top = idealTop
      }
    } else {
      style.top = buttonRect.bottom + POPUP_GAP
      const availableBelow = viewportHeight - buttonRect.bottom - POPUP_GAP - VIEWPORT_PADDING
      if (popupRect.height > availableBelow) {
        style.maxHeight = availableBelow
        style.overflowY = 'auto'
      }
    }

    if (usePortal) {
      const rightValue = viewportWidth - buttonRect.right
      const wouldOverflowLeft = rightValue + popupRect.width > viewportWidth

      if (wouldOverflowLeft) {
        style.left = buttonRect.left
        style.right = 'auto'
      } else {
        style.right = rightValue
        style.left = 'auto'
      }
    } else if (needsFlipHorizontal) {
      style.right = viewportWidth - buttonRect.right
      style.left = 'auto'
    } else {
      style.left = Math.max(0, popupRect.left)
    }

    setAutoPlacementStyle(style)
  }, [isOpen, popupFixedProps, usePortal])

  useLayoutEffect(() => {
    computePlacementStyle()
  }, [computePlacementStyle])

  useEffect(() => {
    if (!isOpen || !usePortal) return

    const handleScroll = (event: Event) => {
      if (wrapperRef.current?.contains(event.target as Node)) return
      computePlacementStyle()
    }

    document.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen, usePortal, computePlacementStyle])

  const boxClasses = cn(
    {
      [styles.Box]: true,
      [styles.BoxOpen]: isOpen,
    },
    classes.Popup,
    popoverProps?.className,
  )

  const itemProps = needToPassChildToggleProps
    ? {
        toggled: isOpen,
        togglePopup,
      }
    : {}

  return (
    <PopupButtonProvider isHovered={isHovered} isOpen={isOpen} toggle={handleTogglePopup}>
      <div
        {...componentHoverProps}
        className={cn(styles.PopupButton, className)}
        title={title}
        data-testid={dataTestId}
        data-pendo={dataPendo}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={event => {
            event.stopPropagation()
            event.preventDefault()
            if (disabled !== true && !openOnHover) {
              togglePopup()
            }
          }}
          ref={buttonRef}
          className={cn(
            styles.ButtonWrapper,
            { [styles.ButtonWrapperDisabled]: disabled },
            buttonClassName,
          )}
          aria-label={`${title} button`}
          {...hoverProps}
          {...buttonProps}
        >
          {buttonComponent ? (
            React.cloneElement(buttonComponent, itemProps)
          ) : (
            <div className={buttonClasses}>
              <MdMoreVert />
            </div>
          )}
        </div>
        {(() => {
          const portalBaseStyle: React.CSSProperties = usePortal
            ? { right: 'auto', top: 'auto' }
            : {}
          const popupContent = (
            <div
              className={boxClasses}
              ref={wrapperRef}
              style={{ ...fixedStyles, ...portalBaseStyle, ...autoPlacementStyle }}
              {...omit(popoverProps, 'className')}
            >
              {isLazy && !isOpen ? null : React.cloneElement(popupComponent, itemProps)}
            </div>
          )
          return usePortal
            ? createPortal(<div className={styles.PopupButton}>{popupContent}</div>, document.body)
            : popupContent
        })()}
      </div>
    </PopupButtonProvider>
  )
}

export default PopupButton
